import type { FC } from 'react'
import { useRef, useState } from 'react'

import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { useQuery } from '@tanstack/react-query'
import AccountIcon from 'mdi-react/AccountIcon'
import BellIcon from 'mdi-react/BellIcon'
import ChevronDownIcon from 'mdi-react/ChevronDownIcon'

import { notificationResources } from '@/api/notifications'
import queries from '@/api/queries'
import systemMessagesResources from '@/api/system-messages'
import { useAppContext } from '@/App.context'
import SystemMessageList from '@/components/header/SystemMessageList'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import { useFullUserName, useProfile } from '@/hooks/user'
import { SEC } from '@/util/formatters/dates'
import { getFromLocalStorage } from '@/util/persistance'

import AccountDetails from './AccountDetails'

import './account-menu.scss'

const getInitials = (fullName: string) => {
  const nameWithoutSpaces = fullName.split(' ')
  const initials = nameWithoutSpaces.map(name => name[0])

  return initials.join('')
}

export const AccountBar: FC = () => {
  const { locale, whiteLabel } = useAppContext()
  const fullName = useFullUserName()
  const profile = useProfile()
  const dealer = profile?.dealer

  const [isExtended, setIsExtended] = useState(false)
  const [seenMessages, setSeenMessages] = useState<number[] | undefined>(
    getFromLocalStorage('read-system-messages')
  )
  const menuRef = useRef(null)

  useOnClickOutside(menuRef, () => setIsExtended(false))

  const { data: systemMessages } = useQuery(
    queries.systemMessages(),
    systemMessagesResources.getList
  )

  useQuery(queries.notifications(), notificationResources.get, {
    refetchInterval: 20 * SEC,
  })

  const localeBasedMessages = systemMessages?.filter(message => message.locale === locale)
  const displayedMessage = localeBasedMessages?.[0]
  const isNotificationRead = displayedMessage ? seenMessages?.includes(displayedMessage?.id) : false
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)

  return (
    <div
      ref={menuRef}
      className={`account-menu wl-${whiteLabel} ${isExtended ? 'extended' : 'collapsed'} ${environmentBadgeLabel ? 'is-development-mode' : ''}`}
    >
      <div className={`account-menu__details ${isExtended ? 'extended' : 'collapsed'}`}>
        <AccountDetails />

        <SystemMessageList
          seenMessages={seenMessages}
          onSeenMessagesSet={setSeenMessages}
          onBarClose={() => setIsExtended(false)}
        />
      </div>
      <div className="account-menu__toggler">
        <button
          onClick={() => setIsExtended(!isExtended)}
          className={`toggler ${isExtended ? 'is-hidden' : 'is-visible'} ${environmentBadgeLabel ? 'is-development-mode' : ''}`}
        >
          <div>
            <div className="profile-name-section">
              <AccountIcon />

              <span className="full-name">
                {getInitials(fullName)} - {dealer}
              </span>

              <ChevronDownIcon className="chevron" />
            </div>

            {environmentBadgeLabel && (
              <div className="development-indication">{environmentBadgeLabel}</div>
            )}
          </div>

          <span
            className={`system-message-icon ${
              displayedMessage && !isNotificationRead ? 'is-visible' : ''
            }`}
          >
            <BellIcon />
          </span>
        </button>
      </div>
    </div>
  )
}
